/**
 * CraveTheory cart drawer — custom panel (no Horizon theme-drawer).
 * Keeps id="cart-drawer" so header on:click="#cart-drawer/toggle" and
 * Shopify.actions.openCart keep working.
 *
 * Panel motion is driven by the Web Animations API (not CSS transitions/
 * keyframes). Native <dialog>.showModal() often skips CSS motion, and the
 * previous ease-out made the first 100ms look like a jump.
 */
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** Slow enough to read as a slide, not a snap */
const SLIDE_IN_MS = 820;
const SLIDE_OUT_MS = 560;
/** Even travel across the full distance (not front-loaded ease-out) */
const SLIDE_EASE = 'cubic-bezier(0.32, 0.72, 0.28, 1)';

class CtCartDrawer extends Component {
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {Animation | null} */
  #panelAnim = null;

  /** @type {Animation | null} */
  #scrimAnim = null;

  /** @type {number} */
  #closeTimer = 0;

  /** Prevents re-entrant open() from restarting mid-slide */
  #opening = false;

  /** @type {HTMLDialogElement | null} */
  get panel() {
    return this.querySelector('dialog');
  }

  /** @type {HTMLElement | null} */
  get sheet() {
    return this.querySelector('.ct-cart-drawer__panel');
  }

  /** @type {HTMLElement | null} */
  get scrim() {
    return this.querySelector('.ct-cart-drawer__scrim');
  }

  get isOpen() {
    return this.hasAttribute('open') || Boolean(this.panel?.open);
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    super.connectedCallback();

    this.panel?.addEventListener('cancel', this.#onCancel);
    this.panel?.addEventListener('click', this.#onDialogClick);
    this.querySelectorAll('[data-ct-cart-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    if (this.hasAttribute('open')) {
      this.open({ silent: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.panel?.removeEventListener('cancel', this.#onCancel);
    this.panel?.removeEventListener('click', this.#onDialogClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    if (this.#closeTimer) window.clearTimeout(this.#closeTimer);
    this.#panelAnim?.cancel();
    this.#scrimAnim?.cancel();
    this.#opening = false;
  }

  /**
   * @param {Event | { silent?: boolean }} [optionsOrEvent]
   */
  open(optionsOrEvent = {}) {
    const options =
      optionsOrEvent instanceof Event || !optionsOrEvent ? {} : /** @type {{ silent?: boolean }} */ (optionsOrEvent);

    const panel = this.panel;
    const sheet = this.sheet;
    if (!panel || !sheet) return;

    // Already fully open — don't restart
    if (
      this.isOpen &&
      this.classList.contains('is-open') &&
      !this.classList.contains('is-closing') &&
      panel.open &&
      !this.#opening
    ) {
      return;
    }

    if (this.#opening) return;

    if (this.#closeTimer) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = 0;
    }

    this.#opening = true;
    this.#panelAnim?.cancel();
    this.#scrimAnim?.cancel();

    if (!options.silent) {
      this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);
    }

    this.classList.remove('is-closing');
    panel.classList.remove('is-closing');

    this.setAttribute('open', '');
    document.documentElement.classList.add('ct-cart-drawer-open');

    if (typeof panel.showModal === 'function') {
      if (!panel.open) panel.showModal();
    } else if (!panel.hasAttribute('open')) {
      panel.setAttribute('open', '');
    }

    this.classList.add('is-open');
    panel.classList.add('is-open');

    // Start off-screen, then glide in (WAAPI always runs from keyframe 0)
    sheet.style.transform = 'translate3d(100%, 0, 0)';
    const scrim = this.scrim;
    if (scrim) scrim.style.opacity = '0';

    if (this.#reducedMotion) {
      sheet.style.transform = 'translate3d(0, 0, 0)';
      if (scrim) scrim.style.opacity = '1';
      this.#opening = false;
      this.#focusClose(panel, options.silent);
      this.dispatchEvent(new CustomEvent('ct-cart:open', { bubbles: true }));
      return;
    }

    // Double rAF so the off-screen frame paints before the animation starts
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isOpen) {
          this.#opening = false;
          return;
        }

        this.#panelAnim = sheet.animate(
          [{ transform: 'translate3d(100%, 0, 0)' }, { transform: 'translate3d(0, 0, 0)' }],
          {
            duration: SLIDE_IN_MS,
            easing: SLIDE_EASE,
            fill: 'forwards',
          }
        );

        if (scrim) {
          this.#scrimAnim = scrim.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: SLIDE_IN_MS,
            easing: 'ease-out',
            fill: 'forwards',
          });
        }

        this.#panelAnim.finished
          .then(() => {
            sheet.style.transform = 'translate3d(0, 0, 0)';
            this.#panelAnim = null;
          })
          .catch(() => {});

        this.#opening = false;
        this.#focusClose(panel, options.silent);
        this.dispatchEvent(new CustomEvent('ct-cart:open', { bubbles: true }));
      });
    });
  }

  /** @param {Event} [_event] */
  close(_event) {
    const panel = this.panel;
    const sheet = this.sheet;
    if (!panel || !sheet) return;
    if (!this.isOpen && !this.#opening) return;

    this.#opening = false;
    this.#panelAnim?.cancel();
    this.#scrimAnim?.cancel();

    this.classList.remove('is-open');
    panel.classList.remove('is-open');
    this.classList.add('is-closing');
    panel.classList.add('is-closing');
    this.removeAttribute('open');
    document.documentElement.classList.remove('ct-cart-drawer-open');

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (this.#closeTimer) {
        window.clearTimeout(this.#closeTimer);
        this.#closeTimer = 0;
      }
      this.#panelAnim = null;
      this.#scrimAnim = null;

      this.classList.remove('is-closing');
      panel.classList.remove('is-closing');
      sheet.style.transform = 'translate3d(100%, 0, 0)';
      const scrim = this.scrim;
      if (scrim) scrim.style.opacity = '0';

      if (typeof panel.close === 'function' && panel.open) {
        panel.close();
      } else {
        panel.removeAttribute('open');
      }

      const prev = this.#previouslyFocused;
      if (prev && typeof prev.focus === 'function') {
        prev.focus({ preventScroll: true });
      }
      this.#previouslyFocused = null;
      this.dispatchEvent(new CustomEvent('ct-cart:close', { bubbles: true }));
    };

    if (this.#reducedMotion) {
      finish();
      return;
    }

    const computed = getComputedStyle(sheet).transform;
    const startTransform = !computed || computed === 'none' ? 'translate3d(0, 0, 0)' : computed;

    this.#panelAnim = sheet.animate(
      [{ transform: startTransform }, { transform: 'translate3d(100%, 0, 0)' }],
      {
        duration: SLIDE_OUT_MS,
        easing: 'cubic-bezier(0.4, 0, 0.7, 0.2)',
        fill: 'forwards',
      }
    );

    const scrim = this.scrim;
    if (scrim) {
      this.#scrimAnim = scrim.animate([{ opacity: Number(getComputedStyle(scrim).opacity) || 1 }, { opacity: 0 }], {
        duration: SLIDE_OUT_MS,
        easing: 'ease-in',
        fill: 'forwards',
      });
    }

    this.#panelAnim.finished.then(finish).catch(finish);
    this.#closeTimer = window.setTimeout(finish, SLIDE_OUT_MS + 120);
  }

  /** @param {Event} [_event] */
  toggle(_event) {
    if (this.isOpen || this.#opening) this.close();
    else this.open();
  }

  /**
   * @param {HTMLDialogElement} panel
   * @param {boolean} [silent]
   */
  #focusClose(panel, silent) {
    if (silent) return;
    const closeBtn = panel.querySelector('[data-ct-cart-close], .ct-cart-drawer__close');
    if (closeBtn instanceof HTMLElement) {
      requestAnimationFrame(() => closeBtn.focus({ preventScroll: true }));
    }
  }

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    // Clicks on the dialog chrome (not the sheet / scrim handles itself)
    if (event.target === this.panel) {
      this.close();
    }
  };

  /**
   * Open once after a successful add — wait for the cart promise so section
   * morph has settled.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const openAfterSettle = () => {
      // Two frames after morph so the drawer nodes are stable before we animate
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.open());
      });
    };

    if (!event.promise) {
      if (!sourceModal) openAfterSettle();
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (detail?.didError) return;

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAfterSettle, { once: true });
          return;
        }

        openAfterSettle();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ct-cart] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ct-cart-drawer')) {
  customElements.define('ct-cart-drawer', CtCartDrawer);
}

export { CtCartDrawer };
